import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Plus, Trash2, Pencil, Check, X, Home, Briefcase, GraduationCap, Dumbbell, Heart, Tag } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useTranslation } from "react-i18next";

const ICON_FOR_LABEL = (label = "") => {
  const t = label.toLowerCase().trim();
  if (/casa|home|abitazione/.test(t)) return Home;
  if (/uffici|lavor|work/.test(t)) return Briefcase;
  if (/scuola|asilo|nido|liceo/.test(t)) return GraduationCap;
  if (/palestra|sport|basket|calcio|nuoto/.test(t)) return Dumbbell;
  if (/nonn|amic|famig|family/.test(t)) return Heart;
  return Tag;
};

export default function PlacesList() {
  const { t } = useTranslation();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [formLabel, setFormLabel] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/places");
      setPlaces(data.places || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing("new");
    setFormLabel("");
    setFormAddress("");
  };
  const startEdit = (p) => {
    setEditing(p.id);
    setFormLabel(p.label);
    setFormAddress(p.address);
  };
  const cancel = () => {
    setEditing(null);
    setFormLabel("");
    setFormAddress("");
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!formLabel.trim() || !formAddress.trim()) {
      toast.error("Compila nome e indirizzo");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/places", { label: formLabel.trim(), address: formAddress.trim() });
        toast.success("Luogo aggiunto");
      } else {
        await api.put(`/places/${editing}`, { label: formLabel.trim(), address: formAddress.trim() });
        toast.success("Luogo aggiornato");
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.label}"?`)) return;
    try {
      await api.delete(`/places/${p.id}`);
      toast.success("Luogo eliminato");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="anim-slide-up max-w-2xl mx-auto" data-testid="places-page">
      <Link to="/home" className="text-sm font-bold text-[#1A2A6C] inline-flex items-center gap-1 hover:underline mb-3" data-testid="places-back">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#F5F6FA] text-[#1A2A6C]">
          <MapPin className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-3xl font-black text-[#1A1D29] leading-tight">{t("places.title")}</h1>
      </div>
      <p className="text-sm text-[#8A91A8] mb-4">
        {t("places.subtitle")}
      </p>

      {editing == null && (
        <div className="mb-5">
          <button onClick={startCreate} className="brut-btn inline-flex w-full sm:w-auto justify-center" data-testid="places-add">
            <Plus className="w-4 h-4" /> {t("places.add")}
          </button>
        </div>
      )}

      {editing != null && (
        <form onSubmit={save} className="brut-card p-4 sm:p-5 mb-5 space-y-3" data-testid="places-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#8A91A8] mb-1.5">{t("places.name")}</label>
            <input
              type="text"
              className="brut-input"
              placeholder={t("places.namePh")}
              value={formLabel}
              maxLength={80}
              onChange={(e) => setFormLabel(e.target.value)}
              autoFocus
              data-testid="places-form-label"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#8A91A8] mb-1.5">{t("places.address")}</label>
            <AddressAutocomplete value={formAddress} onChange={setFormAddress} placeholder={t("places.addrPh")} testId="places-form-address" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving} className="brut-btn flex-1 justify-center" data-testid="places-form-submit">
              <Check className="w-4 h-4" /> {editing === "new" ? "Aggiungi" : "Salva"}
            </button>
            <button type="button" onClick={cancel} className="brut-btn brut-btn-ghost flex-1 justify-center" data-testid="places-form-cancel">
              <X className="w-4 h-4" /> Annulla
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-center text-[#8A91A8] py-8">Caricamento…</p>
      ) : places.length === 0 && editing == null ? (
        <div className="brut-card p-8 text-center">
          <p className="text-[#8A91A8] mb-4">{t("places.empty")}</p>
          <button onClick={startCreate} className="brut-btn inline-flex">
            <Plus className="w-4 h-4" /> {t("places.addFirst")}
          </button>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="places-list">
          {places.map((p) => {
            const Icon = ICON_FOR_LABEL(p.label);
            return (
              <li key={p.id} className="brut-card p-4 flex items-center gap-3" data-testid={`place-row-${p.id}`}>
                <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#F5F6FA] text-[#1A2A6C] shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-[#1A1D29] truncate">{p.label}</p>
                  <p className="text-xs text-[#8A91A8] truncate">{p.address}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => startEdit(p)} className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]" aria-label="Modifica" data-testid={`place-edit-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(p)} className="w-9 h-9 grid place-items-center rounded-xl bg-[#FFE6E9] text-[#FF4D5E] hover:bg-[#FFD0D5]" aria-label="Elimina" data-testid={`place-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
